import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api";
import { resetPassword } from "@/lib/auth-api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

type PageState = "idle" | "loading" | "success" | { kind: "error"; message: string };

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [pageState, setPageState] = useState<PageState>("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) {
      setPageState({ kind: "error", message: t("resetPassword.errorInvalidToken") });
      return;
    }
    setPageState("loading");
    try {
      await resetPassword(token, password);
      setPageState("success");
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message || t("resetPassword.errorGeneric") : t("resetPassword.errorGeneric");
      setPageState({ kind: "error", message });
    }
  }

  const isLoading = pageState === "loading";

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <div className="flex items-center justify-between px-8 pt-6">
        <Link to="/login?tab=signin">
          <Logo />
        </Link>
        <LanguageSwitcher
          currentLang={i18n.language}
          onChange={(l) => void i18n.changeLanguage(l)}
          variant="compact"
        />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-8 pb-12">
        <div className="w-full max-w-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {t("resetPassword.eyebrow")}
          </p>

          {pageState === "success" ? (
            <div data-testid="reset-password-success">
              <h1 className="mt-2 font-display text-2xl font-bold text-brand-primary">
                {t("resetPassword.successTitle")}
              </h1>
              <p className="mt-3 text-sm text-neutral-600">
                {t("resetPassword.successBody")}
              </p>
              <Link
                to="/login?tab=signin"
                className="mt-6 inline-block rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              >
                {t("resetPassword.loginCta")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mt-2 font-display text-2xl font-bold text-brand-primary">
                {t("resetPassword.title")}
              </h1>
              <p className="mt-1.5 text-sm text-neutral-600">
                {t("resetPassword.subtitle")}
              </p>

              {typeof pageState === "object" && pageState.kind === "error" && (
                <div
                  role="alert"
                  className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {pageState.message}
                </div>
              )}

              {!token ? (
                <div
                  role="alert"
                  className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {t("resetPassword.errorInvalidToken")}
                </div>
              ) : (
                <form onSubmit={(e) => void handleSubmit(e)} noValidate className="mt-6">
                  <label
                    htmlFor="reset-password"
                    className="mb-1 block text-xs font-medium text-neutral-700"
                  >
                    {t("resetPassword.passwordLabel")}
                  </label>
                  <input
                    id="reset-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-brand-primary placeholder-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-60"
                  />

                  <button
                    type="submit"
                    disabled={isLoading || !password}
                    className="mt-4 w-full rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:opacity-50"
                  >
                    {isLoading ? t("resetPassword.submitting") : t("resetPassword.submit")}
                  </button>
                </form>
              )}

              <Link
                to="/login?tab=signin"
                className="mt-4 block text-center text-sm font-medium text-neutral-600 underline hover:text-neutral-800"
              >
                {t("forgotPassword.backToLogin")}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
